import { useMemo } from 'react';
import type { VoyageCharterPortCostsQueryVariables } from '../../graphql';
import {
  useVoyageCharterPortCostsQuery,
  VoyageCharterPortCostsDocument,
  type VoyageCharterPortCostsQuery,
} from '../../graphql';
import { isDefined } from '../../lib/util';
import type { ApolloClient } from '@apollo/client';

function parsePortCostsByVc(
  portCostsByVc: VoyageCharterPortCostsQuery | undefined,
) {
  const portCostByVcMap = new Map<
    `${number}-${number}`,
    {
      cost: number;
      currency: string;
      estimatedCost?: number;
    }
  >();

  const filterPortCostsByVc =
    portCostsByVc?.allVoyageCharterPortCosts?.nodes.filter(isDefined);

  if (!filterPortCostsByVc) return portCostByVcMap;

  for (const pc of filterPortCostsByVc) {
    const vcId = pc.voyageCharterId;
    const locationId = pc.portCostByPortCostId?.locationId;
    const cost = pc.portCostByPortCostId?.cost;
    const estimatedCost = pc.portCostByPortCostId?.estimatedCost;
    const currency =
      pc.portCostByPortCostId?.portCostCurrency ||
      pc.voyageCharterByVoyageCharterId?.currency ||
      'EUR';
    if (!locationId || !cost || !currency) continue;
    portCostByVcMap.set(`${vcId}-${locationId}`, {
      cost: +cost,
      currency,
      estimatedCost: estimatedCost ? +estimatedCost : undefined,
    });
  }
  return portCostByVcMap;
}

export async function getPortCostsByVc(client: ApolloClient<unknown>) {
  const { data: portCostsByVc } = await client.query<
    VoyageCharterPortCostsQuery,
    VoyageCharterPortCostsQueryVariables
  >({
    query: VoyageCharterPortCostsDocument,
  });
  return parsePortCostsByVc(portCostsByVc);
}

export function usePortCosts(shouldSkip = false) {
  const { data: portCostsByVc } = useVoyageCharterPortCostsQuery({
    skip: shouldSkip,
  });

  return useMemo(() => parsePortCostsByVc(portCostsByVc), [portCostsByVc]);
}
